// @ts-nocheck

import { isLowPowerDevice } from '@theme/utilities';

/**
 * 🆕 NOVO — TOMI Marquee Carousel
 *
 * Carrossel de rolagem 100% livre e contínua, sem scroll-snap. Ao contrário do
 * carrossel nativo do tema (<slideshow-component>, que usa scroll-snap-type e
 * "pula"/alinha pro slide mais próximo a cada passo), este é um scroll nativo
 * comum (overflow-x: auto) sem nenhuma propriedade de snap — arrastar com o
 * dedo, mouse ou trackpad acompanha o movimento 1:1, livre, sem travar em
 * nenhuma seção.
 *
 * TOMI — reescrito (2ª vez): o autoplay agora é uma animação CSS pura
 * (@keyframes translateX, ver tomi-marquee-carousel.liquid) em vez de JS
 * escrevendo scrollLeft a cada frame via requestAnimationFrame. Animações CSS
 * de transform rodam na thread de composição do navegador — são IMUNES a
 * qualquer travamento da thread principal (JS pesado de outros componentes,
 * timers, etc.), o que é exatamente a causa dos engasgos "a cada segundo"
 * relatados mesmo depois da 1ª otimização (que só evitava forced-layout, mas
 * ainda dependia da thread principal rodar every single frame). Arrastar
 * manualmente (touch nativo + drag de mouse) continua 100% baseado em
 * scrollLeft, exatamente como antes — os dois mecanismos são independentes e
 * se somam visualmente sem conflito (a animação simplesmente pausa/retoma
 * via animation-play-state durante uma interação, preservando a posição).
 * O conteúdo continua renderizado 3x em sequência (ver
 * snippets/tomi-marquee-carousel.liquid) — 1/3 da largura é uma volta
 * completa, usada tanto pelo loop da animação CSS quanto pela recentralização
 * do scrollLeft durante o arraste manual.
 */
class TomiMarqueeCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {number} */
  #loopWidth = 0;

  /** @type {number | null} */
  #resumeTimer = null;

  /** @type {boolean} */
  #autoplay = false;

  /** @type {number} */
  #speedPxPerSecond = 40;

  /** @type {number} */
  #resumeDelay = 6000;

  /** @type {AbortController | undefined} */
  #dragAbortController;

  connectedCallback() {
    this.#track = this.querySelector('[data-tomi-marquee-track]');
    if (!this.#track) return;

    // TOMI — o autoplay nativo do tema (assets/slideshow.js) NÃO desativa a
    // rolagem automática por causa de "prefers-reduced-motion" (só usa isso
    // pra pular a transição suave entre slides) — mantemos o mesmo padrão
    // aqui, senão o autoplay simplesmente não aparece pra ninguém com essa
    // preferência do sistema ativada (inclusive, possivelmente, no preview
    // do próprio lojista), o que gera a impressão de "não está funcionando".
    // TOMI — em dispositivos de baixo desempenho, ainda desativamos de
    // verdade (diferente do reduced-motion acima) — mesmo uma animação CSS
    // barata não vale a pena forçar em hardware muito fraco. O carrossel
    // continua 100% navegável manualmente (arrastar/tocar).
    this.#autoplay = this.dataset.autoplay === 'true' && !isLowPowerDevice();
    const speedSeconds = parseFloat(this.dataset.autoplaySpeed ?? '4');
    // "segundos por card" -> px/s aproximado usando a largura real do 1º item
    const firstItem = this.#track.firstElementChild;
    const itemWidth = firstItem instanceof HTMLElement ? firstItem.getBoundingClientRect().width : 280;
    const gap = parseFloat(getComputedStyle(this.#track).gap || '16');
    this.#speedPxPerSecond = Number.isFinite(speedSeconds) && speedSeconds > 0 ? (itemWidth + gap) / speedSeconds : 40;
    this.#resumeDelay = parseInt(this.dataset.resumeDelay ?? '6000', 10);

    // Espera o layout assentar antes de medir e centralizar a posição inicial.
    requestAnimationFrame(() => {
      this.#measureAndCenter();
      this.#bindInteraction();
      if (this.#autoplay) this.#enableAutoplayAnimation();
    });

    new ResizeObserver(() => this.#measureAndCenter()).observe(this);
  }

  disconnectedCallback() {
    this.#dragAbortController?.abort();
    window.clearTimeout(this.#resumeTimer ?? undefined);
  }

  #measureAndCenter() {
    if (!this.#track) return;
    // O track tem 3 cópias idênticas do conteúdo — 1/3 da largura total é
    // exatamente uma volta completa do loop.
    this.#loopWidth = this.#track.scrollWidth / 3;
    if (this.#loopWidth <= 0) return;

    // Só recentraliza na primeira medição (scrollLeft ainda em 0) pra não
    // saltar a posição enquanto o usuário já está navegando.
    if (this.scrollLeft < 1) {
      this.scrollLeft = this.#loopWidth;
    }

    if (this.#autoplay) {
      const durationSeconds = this.#loopWidth / this.#speedPxPerSecond;
      this.style.setProperty('--tomi-marquee-loop-width', `${this.#loopWidth}px`);
      this.style.setProperty('--tomi-marquee-duration', `${durationSeconds}s`);
    }
  }

  #keepCentered() {
    if (this.#loopWidth <= 0) return;

    if (this.scrollLeft < this.#loopWidth * 0.5) {
      this.scrollLeft += this.#loopWidth;
    } else if (this.scrollLeft > this.#loopWidth * 1.5) {
      this.scrollLeft -= this.#loopWidth;
    }
  }

  /**
   * Liga a animação CSS de autoplay uma única vez. Pausar/retomar depois
   * disso é feito só via animation-play-state (#pauseForInteraction /
   * #scheduleResume) — nunca removendo/reaplicando essa flag, o que
   * reiniciaria a animação do zero em vez de preservar a posição.
   */
  #enableAutoplayAnimation() {
    if (this.#loopWidth <= 0) return;
    this.setAttribute('data-tomi-autoplay-active', 'true');
  }

  #pauseForInteraction() {
    window.clearTimeout(this.#resumeTimer ?? undefined);
    if (this.#track) this.#track.style.animationPlayState = 'paused';
  }

  #scheduleResume() {
    if (!this.#autoplay) return;
    window.clearTimeout(this.#resumeTimer ?? undefined);
    this.#resumeTimer = window.setTimeout(() => {
      if (this.#track) this.#track.style.animationPlayState = 'running';
    }, this.#resumeDelay);
  }

  #bindInteraction() {
    const signal = (this.#dragAbortController = new AbortController()).signal;

    // O evento "scroll" só serve aqui pra manter o loop recentralizado
    // (arraste manual), venha a rolagem de onde vier — não tem mais relação
    // nenhuma com o autoplay, que agora é uma animação CSS independente.
    this.addEventListener('scroll', () => this.#keepCentered(), { passive: true, signal });

    this.addEventListener('touchstart', () => this.#pauseForInteraction(), { passive: true, signal });
    this.addEventListener('touchend', () => this.#scheduleResume(), { passive: true, signal });
    this.addEventListener('touchcancel', () => this.#scheduleResume(), { passive: true, signal });

    let wheelEndTimer;
    this.addEventListener(
      'wheel',
      () => {
        this.#pauseForInteraction();
        window.clearTimeout(wheelEndTimer);
        wheelEndTimer = window.setTimeout(() => this.#scheduleResume(), 150);
      },
      { passive: true, signal }
    );

    // Arrastar com MOUSE não rola um <div> nativamente (diferente de toque) —
    // implementa isso manualmente, 1:1 com o cursor, sem nenhum "snap" pro
    // slide mais próximo.
    let dragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    this.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'touch') return;
        dragging = true;
        this.setPointerCapture(event.pointerId);
        startX = event.clientX;
        startScrollLeft = this.scrollLeft;
        this.setAttribute('data-tomi-dragging', 'true');
        this.#pauseForInteraction();
      },
      { signal }
    );

    this.addEventListener(
      'pointermove',
      (event) => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        let target = startScrollLeft - delta;

        // Calcula o wrap-around só com a matemática (sem ler this.scrollLeft
        // de volta) e ajusta o próprio startScrollLeft de referência, pra
        // manter o arraste 1:1 contínuo mesmo quando cruza a costura do loop.
        if (this.#loopWidth > 0) {
          if (target < this.#loopWidth * 0.5) {
            target += this.#loopWidth;
            startScrollLeft += this.#loopWidth;
          } else if (target > this.#loopWidth * 1.5) {
            target -= this.#loopWidth;
            startScrollLeft -= this.#loopWidth;
          }
        }

        this.scrollLeft = target;
      },
      { signal }
    );

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      this.removeAttribute('data-tomi-dragging');
      this.#scheduleResume();
    };

    this.addEventListener('pointerup', endDrag, { signal });
    this.addEventListener('pointercancel', endDrag, { signal });

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) this.#pauseForInteraction();
        else this.#scheduleResume();
      },
      { signal }
    );
  }
}

if (!customElements.get('tomi-marquee-carousel')) {
  customElements.define('tomi-marquee-carousel', TomiMarqueeCarousel);
}
